import React, { useState, useMemo } from "react";
import { Html, Line } from "@react-three/drei";
import * as THREE from "three";
import { useThree } from "@react-three/fiber";
import PropTypes from "prop-types";

const PieSlice = ({
  radius,
  angle,
  height,
  color,
  startAngle,
  label,
  value,
  centerOffset = 0.001,
  gapAngle = 0.15,
  position = [0, 0, 0],
  onClick,
  onLabelClick,
  index,

}) => {
  const { camera } = useThree();
  const [hovered, setHovered] = useState(false);

   //Label
  const isOverlap = angle < 0.45;
  let dynamicShift = "-50%"; // default center

  if (isOverlap) {
    dynamicShift = index % 2 === 0 ? "-90%" : "-10%"; // L/R
  }

  // Calculate geometry and line points
  const { geometry, anchor, bend, end } = useMemo(() => {
    const adjustedAngle = Math.max(angle - gapAngle, 0.01);
    const endAngle = startAngle + adjustedAngle;

    // Pie slice shape (unchanged)
    const shape = new THREE.Shape();
    const outerStart = new THREE.Vector2(
      Math.cos(startAngle) * radius,
      Math.sin(startAngle) * radius
    );
    const outerEnd = new THREE.Vector2(
      Math.cos(endAngle) * radius,
      Math.sin(endAngle) * radius
    );
    const tip = new THREE.Vector2(
      Math.cos(startAngle + adjustedAngle / 2) * centerOffset,
      Math.sin(startAngle + adjustedAngle / 2) * centerOffset
    );

    shape.moveTo(tip.x, tip.y);
    shape.lineTo(outerStart.x, outerStart.y);
    shape.absarc(0, 0, radius, startAngle, endAngle, false);
    shape.lineTo(tip.x, tip.y);

    const extrudeSettings = { steps: 1, depth: height, bevelEnabled: false };
    const geo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geo.rotateX(Math.PI / 2);

    // Calculate line points in 3D space
    const centerAngle = startAngle + adjustedAngle / 2;
    
    // Anchor point (on the edge of the slice)
    const anchorPt = new THREE.Vector3(
      Math.cos(centerAngle) * radius,
      height / 2,
      Math.sin(centerAngle) * radius
    );

    // Bend point (extending outward)
    const bendDistance = radius * 0.5; // Adjust this as needed
    const bendPt = new THREE.Vector3(
      Math.cos(centerAngle) * (radius + bendDistance),
      height + 0.02,
      Math.sin(centerAngle) * (radius + bendDistance)
    );

    // End point (horizontal extension)
    const endDistance = radius * 0.09; // Adjust this as needed
    const endDir = centerAngle > Math.PI ? -1 : 1; // Left or right
    const endPt = new THREE.Vector3(
      bendPt.x + endDir * endDistance,
      height + 0.02,
      bendPt.z
    );

    return { geometry: geo, anchor: anchorPt, bend: bendPt, end: endPt };
  }, [radius, angle, startAngle, height, gapAngle, centerOffset]);

  return (
    <group
      position={position}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
      onClick={onClick}
    >
      {/* 3D Slice */}
      <mesh
        geometry={geometry}
        position={[0, height / 2 + (hovered ? 0.12 : 0), 0]}
        castShadow
      >
        <meshStandardMaterial
          color={color}
          side={THREE.DoubleSide}
          toneMapped={false}
        />
      </mesh>

      {/* Leader line */}
      <Line 
        points={[anchor, bend, end]} 
        color={color} 
        lineWidth={1.5} 
        transparent 
      />

      {/* Label */}
      <Html
        position={end}
        center
        style={{
          pointerEvents: "none",
          transform: `translateX(${dynamicShift}) translateY(-50%)`,
        }}
        distanceFactor={10}
        zIndexRange={1}
      >
        <div 
          className="label-chip" 
          onClick={()=>onLabelClick()}
          style={{ 
            border: `1px solid ${color}`,
            backgroundColor:"white"
          }}
          title={label} // Show full label on hover
          >
          <span>
            {label}
          </span>
          : <span style={{}}> &nbsp; {value}</span>
        </div>
      </Html>
    </group>
  );
};

PieSlice.propTypes = {
  radius: PropTypes.number.isRequired,
  angle: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  startAngle: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  centerOffset: PropTypes.number,
  gapAngle: PropTypes.number,
  position: PropTypes.arrayOf(PropTypes.number),
  onClick: PropTypes.func,
  onLabelClick: PropTypes.func,
};

export default PieSlice;



